import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import { SUBRENTALS_KEY } from './types';
import { Notice } from '../ui/Notice';

/** As `GET /api/subrentals/match/catalog` sends it (the fields the role may see). */
export type Strength = 'pewne' | 'mozliwe';
export type CatalogProposal = {
  priceId: string;
  version: number;
  supplierId?: string;
  supplierName?: string;
  name?: string;
  itemId: string;
  itemName: string;
  strength: Strength;
  checked: boolean;
  linkable: boolean;
  reason?: 'supplier_has_item' | 'same_item_twice' | 'ambiguous';
  alternatives: Array<{ itemId: string; itemName: string; strength: Strength }>;
};
/** The server matches at most this many positions per request (`MAX_OPEN_POSITIONS`); `more` says there were more. */
export const MAX_SHOWN = 500;
type LinkResult = { priceId: string; status: 'linked' | 'already' | 'link_not_possible' | 'price_not_of_item' | 'gone'; message?: string };

export const STRENGTH_LABELS: Record<Strength, string> = { pewne: 'pewne', mozliwe: 'możliwe' };
export const REASON_LABELS: Record<NonNullable<CatalogProposal['reason']>, string> = {
  supplier_has_item: 'Ten dostawca ma już pozycję połączoną z tym sprzętem — wybierz inny albo pomiń.',
  same_item_twice: 'Dwie pozycje tego dostawcy pasują do tego samego sprzętu — połączyć można tylko jedną.',
  ambiguous: 'Pasuje kilka naszych pozycji — wybierz właściwą.',
};

/** „Alcons Audio LR7 (Dostawca A)” — the supplier only when the role sees it (rule 7a). */
export const positionLabel = (p: Pick<CatalogProposal, 'name' | 'supplierName'>) => [p.name || 'Pozycja dostawcy', p.supplierName ? `(${p.supplierName})` : ''].filter(Boolean).join(' ');

type Target = { itemId: string; itemName: string };
type ItemOption = PickerOption & { id: string };

async function searchItems(q: string, signal: AbortSignal): Promise<ItemOption[]> {
  const res = await api<{ items: { id: string; name: string }[] }>(`/api/catalog/items?q=${encodeURIComponent(q)}`, { signal });
  return res.items.slice(0, 20).map((i) => ({ key: i.id, id: i.id, name: i.name, details: [] }));
}

/**
 * „Dopasuj do mojego katalogu” (owner 2026-10-23 21:46 and 21:58): the positions only the suppliers have (all, or
 * `supplierId`'s) with the item of ours each most likely is. Only sure matches are ticked; each row's item can be
 * changed; „Połącz zaznaczone” links them in one request (our item's name and category win, the supplier's name stays
 * as „Nazwa u dostawcy”). Refused rows are reported and skipped; a list out of date (409) is reloaded.
 */
export function CatalogMatchPanel({ supplierId, onClose }: { supplierId?: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const key = [...SUBRENTALS_KEY, 'catalog-match', supplierId ?? 'all'];
  const proposals = useQuery({
    queryKey: key,
    queryFn: () => api<{ proposals: CatalogProposal[]; unmatched: number; more?: boolean; truncated?: boolean }>(`/api/subrentals/match/catalog${supplierId ? `?supplierId=${encodeURIComponent(supplierId)}` : ''}`),
  });
  /** What the person changed: ticked or not, another item. */
  const [ticks, setTicks] = useState<Record<string, boolean>>({});
  const [targets, setTargets] = useState<Record<string, Target>>({});
  const [choosing, setChoosing] = useState<string | null>(null);
  const [report, setReport] = useState<{ linked: number; skipped: Array<{ label: string; message: string }> } | null>(null);
  const list = proposals.data?.proposals ?? [];
  const targetOf = (p: CatalogProposal): Target => targets[p.priceId] ?? { itemId: p.itemId, itemName: p.itemName };
  // a row the server would refuse (the supplier has that item) is never ticked unless another item is chosen
  const blocked = (p: CatalogProposal) => !p.linkable && targetOf(p).itemId === p.itemId;
  const isTicked = (p: CatalogProposal) => !blocked(p) && (ticks[p.priceId] ?? p.checked);
  const ticked = list.filter(isTicked);
  const link = useMutation({
    mutationFn: (rows: CatalogProposal[]) =>
      api<{ results: LinkResult[] }>('/api/subrentals/match/catalog/link', { method: 'POST', body: { links: rows.map((p) => ({ priceId: p.priceId, itemId: targetOf(p).itemId, version: p.version })) } }),
    onSuccess: async ({ results }, rows) => {
      const byId = new Map(rows.map((p) => [p.priceId, p]));
      setReport({
        linked: results.filter((r) => r.status === 'linked' || r.status === 'already').length,
        skipped: results.filter((r) => r.status !== 'linked' && r.status !== 'already').map((r) => ({ label: positionLabel(byId.get(r.priceId) ?? {}), message: r.message ?? 'Pominięto.' })),
      });
      setTicks({});
      setTargets({});
      await queryClient.invalidateQueries({ queryKey: SUBRENTALS_KEY });
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.status === 409) await queryClient.invalidateQueries({ queryKey: key });
    },
  });
  const error = link.error instanceof ApiError ? link.error : null;

  return (
    <section className="catalog-match" aria-label="Dopasuj do mojego katalogu" data-feature-id="subrentals.catalog_match">
      <div className="catalog-match-head">
        <h2>Dopasuj do mojego katalogu</h2>
        <button className="btn small" type="button" onClick={onClose}>
          Zamknij
        </button>
      </div>
      <p className="muted">Pozycje dostawców, których nie ma jeszcze połączonych z naszym sprzętem. Zaznaczone są tylko pewne dopasowania — sprawdź resztę i w razie potrzeby wybierz inny sprzęt.</p>
      {proposals.isLoading && (
        <p className="muted" role="status">
          Szukanie dopasowań…
        </p>
      )}
      {proposals.error && <p className="alert" role="alert">{proposals.error instanceof ApiError ? proposals.error.message : 'Nie udało się wczytać dopasowań'}</p>}
      {report && (
        <Notice onClose={() => setReport(null)}>
          <p>Połączono: {report.linked}.</p>
          {report.skipped.length > 0 && (
            <ul aria-label="Pominięte">
              {report.skipped.map((s) => (
                <li key={s.label + s.message}>
                  <strong>{s.label}</strong>: {s.message}
                </li>
              ))}
            </ul>
          )}
        </Notice>
      )}
      {error && (
        <p className="alert" role="alert">
          {error.status === 409 ? 'Ktoś w międzyczasie zmienił te pozycje — lista jest odświeżona, sprawdź i połącz jeszcze raz.' : error.message}
        </p>
      )}
      {proposals.data?.more && (
        <p className="notice" role="status">
          Pokazano {MAX_SHOWN} — zawęź do dostawcy (otwórz jego katalog i kliknij tam „Dopasuj do mojego katalogu”).
        </p>
      )}
      {proposals.data?.truncated && (
        <p className="notice" role="status">
          Katalogi dostawców mają więcej pozycji, niż można przejrzeć naraz — najnowsze pominięto.
        </p>
      )}
      {proposals.data && list.length === 0 && <p className="muted">Nie ma pozycji do dopasowania.</p>}
      {list.length > 0 && (
        <ul className="catalog-match-list" aria-label="Propozycje dopasowania">
          {list.map((p) => {
            const target = targetOf(p);
            const label = positionLabel(p);
            const options = [{ itemId: p.itemId, itemName: p.itemName, strength: p.strength }, ...p.alternatives];
            const own = targets[p.priceId] && !options.some((o) => o.itemId === target.itemId) ? [{ ...target, strength: null }] : [];
            return (
              <li key={p.priceId} className="catalog-match-row">
                <label className="catalog-match-check">
                  <input type="checkbox" checked={isTicked(p)} disabled={blocked(p)} onChange={(e) => setTicks({ ...ticks, [p.priceId]: e.target.checked })} aria-label={`Połącz: ${label}`} />
                  <span>
                    {label} → mój <strong>{target.itemName}</strong>
                  </span>
                </label>
                {!targets[p.priceId] && <span className={`chip ${p.strength === 'pewne' ? 'accent' : ''}`}>{STRENGTH_LABELS[p.strength]}</span>}
                <select
                  aria-label={`Nasz sprzęt dla: ${label}`}
                  value={target.itemId}
                  onChange={(e) => {
                    if (e.target.value === '') return setChoosing(p.priceId);
                    const o = [...options, ...own].find((x) => x.itemId === e.target.value)!;
                    setTargets({ ...targets, [p.priceId]: { itemId: o.itemId, itemName: o.itemName } });
                    setTicks({ ...ticks, [p.priceId]: true });
                  }}
                >
                  {[...options, ...own].map((o) => (
                    <option key={o.itemId} value={o.itemId}>
                      {o.itemName}
                      {o.strength ? ` (${STRENGTH_LABELS[o.strength]})` : ''}
                    </option>
                  ))}
                  <option value="">Inny sprzęt…</option>
                </select>
                {choosing === p.priceId && (
                  <QuickPicker<ItemOption>
                    label="Inny sprzęt z naszego katalogu"
                    placeholder="Szukaj w katalogu…"
                    search={searchItems}
                    onPick={(o) => {
                      setTargets({ ...targets, [p.priceId]: { itemId: o.id, itemName: o.name } });
                      setTicks({ ...ticks, [p.priceId]: true });
                      setChoosing(null);
                    }}
                  />
                )}
                {p.reason && target.itemId === p.itemId && <p className="catalog-match-reason muted">{REASON_LABELS[p.reason]}</p>}
              </li>
            );
          })}
        </ul>
      )}
      {proposals.data && proposals.data.unmatched > 0 && (
        <p className="muted">
          Bez propozycji: {proposals.data.unmatched} — połącz je ręcznie: „Edytuj” przy pozycji → „To samo co u mnie”.
        </p>
      )}
      {list.length > 0 && (
        <div className="actions">
          <button className="btn primary" type="button" disabled={ticked.length === 0 || link.isPending} onClick={() => link.mutate(ticked)}>
            Połącz zaznaczone ({ticked.length})
          </button>
        </div>
      )}
    </section>
  );
}
